/**
 * AnalyticsErrorHandler — global Angular ErrorHandler that captures
 * uncaught errors as `error_occurred` events for PostHog, then delegates
 * to Angular's default handler so console output is preserved.
 *
 * Stack hashes use a small DJB2 fold so PostHog can group similar errors
 * across users without leaking the full stack as a property.
 */
import { ErrorHandler, Injectable, inject } from '@angular/core';
import { AnalyticsService } from './analytics.service';
import { ANALYTICS_EVENTS } from '../analytics/events';
import { redactErrorText } from '../analytics/redact-error-text';
import { canonicaliseRouteIds } from '../analytics/route-taxonomy';
import { isChunkLoadError, reloadOnceForChunkError } from '../utils/chunk-reload';
import { parseLoginBootstrapHash } from '../utils/login-return-path';

@Injectable({ providedIn: 'root' })
export class AnalyticsErrorHandler implements ErrorHandler {
  private readonly analytics = inject(AnalyticsService);

  handleError(error: unknown): void {
    try {
      const err = this.toError(error);
      const context = this.contextFromUrl();
      const errorType = this.classify(err);
      // An uncaught error's message routinely embeds the current URL, so it
      // gets the same free-text redaction as `error_context`.
      const message = redactErrorText(err.message);
      this.analytics.capture(ANALYTICS_EVENTS.ERROR_OCCURRED, {
        error_type: errorType,
        error_context: context,
        error_message: message,
        error_stack_hash: this.hashStack(err.stack ?? err.message),
      });
      // Also emit PostHog's canonical $exception event so the Error
      // Tracking product activates (auto-groups by stack hash, exposes a
      // dedicated Issues view, links to session replay). The typed
      // ERROR_OCCURRED event above stays — it's the analytics-contract
      // version of the same signal and keeps the typed query surface.
      // `error_message` is the property name the GraphQL client's captures
      // use too, so one PostHog filter covers both producers.
      this.analytics.captureException(err, {
        error_type: errorType,
        error_context: context,
        error_message: message,
      });
    } catch {
      // Never let analytics break error reporting itself.
    }
    // Stale-deploy recovery for chunk failures that happen OUTSIDE router
    // navigation (deferred imports, `await import(...)` in services/handlers,
    // unhandled rejections forwarded by provideBrowserGlobalErrorListeners).
    // Route-level failures are already handled by withNavigationErrorHandler
    // in app.config.ts; both paths share the same loop-guarded reload, so at
    // most one recovery reload fires per re-arm window.
    if (isChunkLoadError(error)) {
      reloadOnceForChunkError();
    }
    // Delegate to Angular default — keeps console.error + dev overlay behaviour.
    console.error(error);
  }

  private toError(error: unknown): Error {
    if (error instanceof Error) return error;
    if (typeof error === 'string') return new Error(error);
    try {
      return new Error(JSON.stringify(error));
    } catch {
      return new Error(String(error));
    }
  }

  private classify(
    err: Error,
  ): 'graphql' | 'network' | 'validation' | 'timeout' | 'websocket' | 'uncaught' {
    const msg = err.message?.toLowerCase() ?? '';
    if (msg.includes('graphql') || err.name === 'CombinedGraphQLErrors') return 'graphql';
    // A blocked or rejected socket arrives here as an unhandled rejection:
    // graphql-ws throws synchronously from the WebSocket constructor, before
    // its own `on.error` handler is attached, so this is the only place a
    // subscriptions outage can be attributed. Three weeks of dead production
    // subscriptions were filed under `uncaught` for want of this branch.
    if (msg.includes('websocket')) return 'websocket';
    if (msg.includes('network') || msg.includes('fetch')) return 'network';
    if (msg.includes('timeout') || msg.includes('timed out')) return 'timeout';
    if (msg.includes('validation') || msg.includes('invalid')) return 'validation';
    return 'uncaught';
  }

  /**
   * The route an error happened on, as a canonicalised path (`/people/:id`).
   *
   * The credential segment of a login bootstrap hash (`#rt=…&t=…`) is removed
   * STRUCTURALLY before anything else touches the string. Stripping the
   * leading `#` first was the bug: it turned `#rt=<token>` into `rt=<token>`,
   * which the delimiter-anchored redactor let through, and five users'
   * refresh tokens reached PostHog under this property.
   */
  private contextFromUrl(): string {
    try {
      const hash = window.location.hash || '';
      const bootstrap = parseLoginBootstrapHash(hash);
      const routeHash = bootstrap ? bootstrap.routeHash : hash;
      // The fragment WAS the credential — there is no route to report.
      if (bootstrap && !routeHash) return 'login-bootstrap';
      const path = routeHash.replace(/^#/, '') || window.location.pathname;
      return redactErrorText(canonicaliseRouteIds(path)) || 'unknown';
    } catch {
      return 'unknown';
    }
  }

  /** Short DJB2 hash of the stack — enough to group similar errors in PostHog. */
  private hashStack(stack: string): string {
    let h = 5381;
    for (let i = 0; i < stack.length; i++) {
      h = ((h << 5) + h + stack.charCodeAt(i)) | 0;
    }
    return (h >>> 0).toString(16).padStart(8, '0').slice(0, 12);
  }
}
